import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type NestState, type NestZone, type NestMode } from '@/lib/api';
import { X, Flame, Minus, Plus, Snowflake, Power, Leaf } from 'lucide-react';

// Nest SDM rate-limits SetHeat per device. Bursts of +/- taps trigger 429.
// Debounce so rapid taps coalesce into one command after the user stops.
const SETPOINT_DEBOUNCE_MS = 900;

interface NestDetailProps {
  onClose: () => void;
}

function tempLabel(c: number | null): string {
  if (c === null || Number.isNaN(c)) return '—';
  return `${c.toFixed(1)}°`;
}

function StatusIcon({ status }: { status: NestZone['hvacStatus'] }) {
  if (status === 'HEATING') return <Flame size={12} className="text-tomato" fill="currentColor" />;
  if (status === 'COOLING') return <Snowflake size={12} className="text-tealSoft" />;
  return <Power size={12} className="text-inkFaded" />;
}

export function NestDetail({ onClose }: NestDetailProps) {
  const qc = useQueryClient();
  const nest = useQuery<NestState>({
    queryKey: ['nest', 'state'],
    queryFn: () => api.getNestState(),
    refetchInterval: () => (document.visibilityState === 'visible' ? 15_000 : false),
    staleTime: 5_000,
  });

  const [busyDevice, setBusyDevice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-device optimistic setpoint while user is tapping +/-. Cleared once
  // the mutation lands and we get a fresh read from the server.
  const [pending, setPending] = useState<Record<string, number>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const control = useMutation({
    mutationFn: ({
      action,
      deviceName,
      heatCelsius,
      mode,
      ecoMode,
    }: {
      action: 'setHeat' | 'setMode' | 'setEco';
      deviceName: string;
      heatCelsius?: number;
      mode?: NestMode;
      ecoMode?: 'OFF' | 'MANUAL_ECO';
    }) => {
      setBusyDevice(deviceName);
      return api.nestControl(action, { deviceName, heatCelsius, mode, ecoMode });
    },
    onSettled: () => setBusyDevice(null),
    onSuccess: (_data, vars) => {
      setError(null);
      // Drop optimistic value for this device; the refetch will land soon.
      setPending((p) => {
        const { [vars.deviceName]: _drop, ...rest } = p;
        return rest;
      });
      qc.invalidateQueries({ queryKey: ['nest', 'state'] });
    },
    onError: (e, vars) => {
      // Roll back the optimistic value so the UI matches reality.
      setPending((p) => {
        const { [vars.deviceName]: _drop, ...rest } = p;
        return rest;
      });
      setError(e instanceof Error ? e.message : 'Control failed');
    },
  });

  // Bump setpoint by `delta`, locally, then debounce-fire one SetHeat command.
  function nudgeSetpoint(z: NestZone, delta: number) {
    const current = pending[z.deviceName] ?? z.setpointC ?? 18;
    const next = Math.max(5, Math.min(28, +(current + delta).toFixed(1)));
    setPending((p) => ({ ...p, [z.deviceName]: next }));
    clearTimeout(timers.current[z.deviceName]);
    timers.current[z.deviceName] = setTimeout(() => {
      control.mutate({
        action: 'setHeat',
        deviceName: z.deviceName,
        heatCelsius: next,
      });
    }, SETPOINT_DEBOUNCE_MS);
  }

  // Clean up debounce timers on unmount so we don't leak or fire after close.
  useEffect(() => {
    const t = timers.current;
    return () => {
      Object.values(t).forEach((id) => clearTimeout(id));
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const zones = nest.data?.zones ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-lg shadow-lg overflow-hidden max-h-[90vh] overflow-y-auto bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card/95 backdrop-blur px-5 py-4 border-b border-black/[0.06] flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Flame size={16} className="text-tomato" />
            <h2 className="font-serif italic text-display-sm text-ink">Heating</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-black/5"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {nest.isLoading && zones.length === 0 ? (
            <p className="text-body-sm text-inkFaded">Reading thermostats…</p>
          ) : zones.length === 0 ? (
            <p className="font-serif italic text-inkFaded text-body-lg">
              No Nest thermostats found.
            </p>
          ) : (
            zones.map((z) => {
              const busy = control.isPending && busyDevice === z.deviceName;
              const isOff = z.mode === 'OFF';
              const pendingSet = pending[z.deviceName];
              const displaySetpoint = pendingSet ?? z.setpointC;
              const hasPending = pendingSet !== undefined;
              return (
                <div
                  key={z.deviceName}
                  className="rounded-md border border-black/[0.06] bg-cardWarm p-4 flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                      <span className="font-serif text-[18px] text-ink leading-tight">
                        {z.name}
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] text-inkFaded mt-0.5">
                        <StatusIcon status={z.hvacStatus} />
                        <span>
                          {z.hvacStatus === 'HEATING'
                            ? 'heating'
                            : z.hvacStatus === 'COOLING'
                              ? 'cooling'
                              : 'idle'}
                        </span>
                        {z.humidityPct !== null && (
                          <span className="text-inkGhost">· {Math.round(z.humidityPct)}% RH</span>
                        )}
                        {!z.online && (
                          <span className="text-tomatoDeep font-medium">· offline</span>
                        )}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-[28px] text-ink leading-none">
                        {tempLabel(z.ambientTempC)}
                      </div>
                      <div className="text-[10px] text-inkFaded mt-1">now</div>
                    </div>
                  </div>

                  {/* Setpoint with +/- — debounced (Nest rate-limits SetHeat). */}
                  {!isOff && displaySetpoint !== null && (
                    <div className="flex items-center gap-3 bg-card rounded-md px-3 py-2">
                      <button
                        type="button"
                        aria-label="Decrease temperature"
                        onClick={() => nudgeSetpoint(z, -0.5)}
                        className="w-9 h-9 rounded-full bg-cardWarm hover:bg-peachSoft text-ink flex items-center justify-center"
                      >
                        <Minus size={16} />
                      </button>
                      <div className="flex-1 text-center">
                        <div className={`font-serif text-[24px] leading-none ${hasPending ? 'text-tomato' : 'text-ink'}`}>
                          {tempLabel(displaySetpoint)}
                        </div>
                        <div className="text-[9px] uppercase tracking-[0.14em] text-inkFaded mt-1">
                          {hasPending ? (busy ? 'saving…' : 'pending…') : 'setpoint'}
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label="Increase temperature"
                        onClick={() => nudgeSetpoint(z, 0.5)}
                        className="w-9 h-9 rounded-full bg-cardWarm hover:bg-peachSoft text-ink flex items-center justify-center"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  )}

                  {/* Mode toggles */}
                  <div className="flex gap-1.5">
                    {z.availableModes.includes('HEAT') && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          control.mutate({
                            action: 'setMode',
                            deviceName: z.deviceName,
                            mode: 'HEAT',
                          })
                        }
                        className={`flex-1 px-3 py-1.5 rounded-full text-[11px] tracking-[0.12em] uppercase transition-colors disabled:opacity-40 ${
                          z.mode === 'HEAT' && z.ecoMode !== 'MANUAL_ECO'
                            ? 'bg-tomato text-card'
                            : 'bg-card text-inkSoft hover:bg-peachSoft'
                        }`}
                      >
                        <Flame size={12} className="inline mb-px mr-1" /> Heat
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        control.mutate({
                          action: 'setEco',
                          deviceName: z.deviceName,
                          ecoMode: z.ecoMode === 'MANUAL_ECO' ? 'OFF' : 'MANUAL_ECO',
                        })
                      }
                      className={`flex-1 px-3 py-1.5 rounded-full text-[11px] tracking-[0.12em] uppercase transition-colors disabled:opacity-40 ${
                        z.ecoMode === 'MANUAL_ECO'
                          ? 'bg-sage text-card'
                          : 'bg-card text-inkSoft hover:bg-cardWarm'
                      }`}
                    >
                      <Leaf size={12} className="inline mb-px mr-1" /> Eco
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        control.mutate({
                          action: 'setMode',
                          deviceName: z.deviceName,
                          mode: 'OFF',
                        })
                      }
                      className={`flex-1 px-3 py-1.5 rounded-full text-[11px] tracking-[0.12em] uppercase transition-colors disabled:opacity-40 ${
                        z.mode === 'OFF'
                          ? 'bg-inkSoft text-card'
                          : 'bg-card text-inkSoft hover:bg-cardWarm'
                      }`}
                    >
                      <Power size={12} className="inline mb-px mr-1" /> Off
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {error && (
            <div className="text-[11px] leading-snug px-3 py-2 rounded bg-rose-50 border border-rose-200/40 text-tomatoDeep">
              {error}
            </div>
          )}

          {/* Hot water note — SDM doesn't cleanly expose UK Heat Link hot-water
              controls. The thermostat traits we get back don't include a
              HotWater trait. Set expectations honestly. */}
          <div className="text-[10px] leading-snug text-inkFaded border-t border-black/[0.04] pt-3">
            Hot water from the Heat Link Plus isn't exposed by Google's API yet.
            Use the Nest or Google Home app for the immersion / schedule.
          </div>
        </div>
      </div>
    </div>
  );
}
